import React from 'react';
import { useLocation } from 'react-router-dom';
import { fromUnixTime } from 'date-fns';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';

import {
    useChrono,
    useCurrentTracklog,
    useDebriefApp,
    useDebriefSegments,
    useAnalytics,
} from 'ff/debrief/hooks';
import { AxisId } from 'ff/debrief/models';
import { getCurrentPage } from 'ff/debrief/utils';

import { getDownSampler, ColorBadge } from './utils';
import { AxisSelectField, AxisSelectForm, axisConfig } from './axis';
import FlightChart from './FlightChart';
import SegmentMarkers from 'ff/debrief/components/charts/SegmentMarkers';

const AltitudeSpeedChart = React.forwardRef<HTMLDivElement, {}>((_, ref) => {
    const location = useLocation();
    const { isDecluttering } = useDebriefApp();
    const { currentTime, currentPoint } = useChrono();
    const { points, status, getAvailableDataPoints } = useCurrentTracklog();
    const { selectedSegment, segmentTree } = useDebriefSegments();
    const { logEvent } = useAnalytics();

    const [leftAxis, setLeftAxis] = React.useState<AxisId>('altitude');
    const [rightAxis, setRightAxis] = React.useState<AxisId>('ground_speed');

    const dividerRef = React.useRef<HTMLDivElement>(null);

    const dataPoints = isDecluttering
        ? selectedSegment?.points || points?.data
        : points?.data;

    const leftDataPoints = React.useMemo<[Date[], number[]]>(() => {
        if (!status.isSuccess) return null;
        return getDownSampler(leftAxis)(dataPoints);
    }, [status.isSuccess, selectedSegment?.id, isDecluttering, leftAxis]);

    const rightDataPoints = React.useMemo<[Date[], number[]]>(() => {
        if (!status.isSuccess) return null;
        return getDownSampler(rightAxis)(dataPoints);
    }, [status.isSuccess, selectedSegment?.id, isDecluttering, rightAxis]);

    const currentDateTime = React.useMemo(() => {
        return fromUnixTime(currentTime);
    }, [currentTime]);

    const handleLeftAxisSelect = (selectedAxis: AxisId) => {
        logEvent('Debrief, Profile Axis Data Selected', {
            axis: 'left',
            originalValue: axisConfig[leftAxis].label,
            newValue: axisConfig[selectedAxis].label,
            currentPage: getCurrentPage(location.pathname),
        });

        if (selectedAxis === rightAxis) {
            setRightAxis(leftAxis);
        }
        setLeftAxis(selectedAxis);
    };

    const handleRightAxisSelect = (selectedAxis: AxisId) => {
        logEvent('Debrief, Profile Axis Data Selected', {
            axis: 'right',
            originalValue: axisConfig[rightAxis].label,
            newValue: axisConfig[selectedAxis].label,
            currentPage: getCurrentPage(location.pathname),
        });

        if (selectedAxis === leftAxis) {
            setLeftAxis(rightAxis);
        }
        setRightAxis(selectedAxis);
    };

    if (!points) return null;
    return (
        <Box ref={ref} sx={{ position: 'relative', px: 1 }}>
            <AxisSelectForm ref={dividerRef}>
                <AxisSelectField
                    selectedAxisId={leftAxis}
                    onAxisSelect={handleLeftAxisSelect}
                >
                    {getAvailableDataPoints().map(axis => (
                        <MenuItem key={axis} value={axis}>
                            <ColorBadge color={axisConfig[axis].color} />
                            {axisConfig[axis].label}
                        </MenuItem>
                    ))}
                </AxisSelectField>
                <AxisSelectField
                    selectedAxisId={rightAxis}
                    onAxisSelect={handleRightAxisSelect}
                >
                    {getAvailableDataPoints().map(axis => (
                        <MenuItem key={axis} value={axis}>
                            <ColorBadge color={axisConfig[axis].color} />
                            {axisConfig[axis].label}
                        </MenuItem>
                    ))}
                </AxisSelectField>
            </AxisSelectForm>
            <FlightChart
                timestamps={leftDataPoints[0]}
                currentTime={currentDateTime}
                currentPoint={currentPoint}
                leftAxisId={leftAxis}
                rightAxisId={rightAxis}
                leftAxisData={leftDataPoints[1]}
                rightAxisData={rightDataPoints[1]}
                tooltipBounds={dividerRef}
            >
                {segmentTree && (
                    <SegmentMarkers
                        currentSegment={selectedSegment}
                        segments={segmentTree.getLeafNodes(
                            selectedSegment && {
                                start: leftDataPoints[0].at(0),
                                end: leftDataPoints[0].at(-1),
                            }
                        )}
                    />
                )}
            </FlightChart>
        </Box>
    );
});

AltitudeSpeedChart.displayName = 'AltitudeSpeedChart';

export default AltitudeSpeedChart;
